import type { ComponentType, ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

type RequireAuthenticatedAuthProps = {
  children: ReactNode;
};

/** Route guard for any signed-in member or venue account. */
export function RequireAuthenticatedAuth({ children }: RequireAuthenticatedAuthProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center" role="status">
        <Loader2 className="animate-spin text-[#ff3366]" size={32} aria-hidden />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate to="/login" replace state={{ from: returnTo, signIn: true }} />
    );
  }

  return <>{children}</>;
}

export function withAuthenticatedAuth<P extends object>(Component: ComponentType<P>) {
  function AuthenticatedRoute(props: P) {
    return (
      <RequireAuthenticatedAuth>
        <Component {...props} />
      </RequireAuthenticatedAuth>
    );
  }
  AuthenticatedRoute.displayName = `AuthenticatedAuth(${Component.displayName ?? Component.name ?? "Component"})`;
  return AuthenticatedRoute;
}
